import React, { useEffect, useState } from "react";
import { GetDataWithToken } from "../constants/ApiHelper";
import Layout from "../common/Layout";

const InsurerDocuments = () => {
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // navigation state
  const [pathIndex, setPathIndex] = useState(0); // root folder path segments
  const [selectedInsurer, setSelectedInsurer] = useState(null); // insurer folder open
  const [selectedFile, setSelectedFile] = useState(null); // file card open (details)

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await GetDataWithToken(
        `https://uatmasterapi.flaskitsolutions.com/s3/documents`
      );
      const level1 = response?.data ?? response;
      const finalData = level1?.data ?? level1;
      setDocuments(finalData);
    } catch (err) {
      console.log(err);
      setError("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  };

  // e.g. "master-document/insurer" -> ["master-document", "insurer"]
  const folderSegments = documents?.folder ? documents.folder.split("/") : [];

  const resetNav = () => {
    setPathIndex(0);
    setSelectedInsurer(null);
    setSelectedFile(null);
  };

  const openNextFolder = () => setPathIndex((p) => p + 1);

  const goBack = () => {
    if (selectedFile) {
      setSelectedFile(null);
    } else if (selectedInsurer) {
      setSelectedInsurer(null);
    } else if (pathIndex > 0) {
      setPathIndex((p) => p - 1);
    }
  };

  // ---- Breadcrumb ----
  const renderBreadcrumb = () => {
    const crumbs = ["Home"];
    folderSegments.slice(0, pathIndex).forEach((seg) => crumbs.push(seg));
    if (pathIndex >= folderSegments.length) crumbs.push("Insurers");
    if (selectedInsurer) crumbs.push(selectedInsurer.insurer);
    if (selectedFile) crumbs.push(selectedFile.fileName);

    return (
      <div className="mb-3" style={{ fontSize: 14 }}>
        {crumbs.map((c, i) => (
          <span key={i}>
            <span
              className={i === crumbs.length - 1 ? "fw-semibold" : "text-primary"}
              style={{ cursor: i === crumbs.length - 1 ? "default" : "pointer" }}
              onClick={() => {
                if (i === 0) {
                  resetNav();
                } else if (c === selectedFile?.fileName) {
                  // clicking current file crumb - do nothing
                } else if (c === selectedInsurer?.insurer) {
                  setSelectedFile(null);
                } else {
                  setSelectedFile(null);
                  setSelectedInsurer(null);
                  setPathIndex(Math.min(i, folderSegments.length));
                }
              }}
            >
              {c}
            </span>
            {i < crumbs.length - 1 && <span className="mx-2 text-muted">/</span>}
          </span>
        ))}
      </div>
    );
  };

  // ---- Reusable folder/file card (screenshot jaisa design) ----
  const FolderCard = ({ icon, title, subtitle, badge, onClick }) => (
    <div
      onClick={onClick}
      className="d-flex flex-column align-items-center justify-content-center text-center shadow-sm position-relative"
      style={{
        width: 220,
        minHeight: 170,
        border: "1px solid #eee",
        borderRadius: 16,
        background: "#fff",
        cursor: onClick ? "pointer" : "default",
        padding: 20,
        transition: "transform .15s ease, box-shadow .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 18px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      {badge && (
        <span
          className="badge bg-success position-absolute"
          style={{ top: 10, right: 10, fontSize: 10 }}
        >
          {badge}
        </span>
      )}
      <div style={{ fontSize: 50, marginBottom: 12 }}>{icon}</div>
      <h6
        className="mb-1 text-primary"
        style={{
          wordBreak: "break-word",
          maxWidth: "100%",
        }}
      >
        {title}
      </h6>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
  );

  return (
    <Layout>
      <div className="mx-5 mt-7">
        <h2 className="mb-4">📂 Insurer Documents</h2>

        {loading && <h4 className="text-center mt-5">Loading...</h4>}
        {!loading && error && <h5 className="text-danger text-center">{error}</h5>}

        {!loading && !error && documents && (
          <>
            {(pathIndex > 0 || selectedInsurer) && renderBreadcrumb()}

            {(pathIndex > 0 || selectedInsurer) && (
              <button className="btn btn-sec mb-3" onClick={goBack}>
                ⬅ Back
              </button>
            )}

            {/* STEP 1: root folder path ek-ek karke khulenge */}
            {pathIndex < folderSegments.length && !selectedInsurer && (
              <div className="d-flex flex-wrap gap-3">
                <FolderCard
                  icon="📁"
                  title={folderSegments[pathIndex]}
                  subtitle={
                    pathIndex === folderSegments.length - 1
                      ? `${documents.totalInsurers} Insurers`
                      : "1 Records"
                  }
                  onClick={openNextFolder}
                />
              </div>
            )}

            {/* STEP 2: insurer folders (digit, kotak) */}
            {pathIndex >= folderSegments.length && !selectedInsurer && (
              <div className="d-flex flex-wrap gap-3">
                {documents.insurers?.map((ins, i) => (
                  <FolderCard
                    key={i}
                    icon="📁"
                    title={ins.insurer}
                    subtitle={`${ins.total} Documents`}
                    onClick={() => setSelectedInsurer(ins)}
                  />
                ))}
              </div>
            )}

            {/* STEP 3: selected insurer ki files - ab folder card style me */}
            {selectedInsurer && !selectedFile && (
              <div className="d-flex flex-wrap gap-3">
                {selectedInsurer.files.map((file, i) => (
                  <FolderCard
                    key={i}
                    icon="📄"
                    title={file.fileName}
                    subtitle={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    badge={file.isLatest ? "Latest" : null}
                    onClick={() => setSelectedFile(file)}
                  />
                ))}
              </div>
            )}

            {/* STEP 4: file card click -> details + download */}
            {selectedFile && (
              <div
                className="card shadow-sm"
                style={{ maxWidth: 480 }}
              >
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div style={{ fontSize: 40, width: 55 }}>📄</div>
                    <div>
                      <div className="fw-semibold" style={{ wordBreak: "break-word" }}>
                        {selectedFile.fileName}
                        {selectedFile.isLatest && (
                          <span className="badge bg-success ms-2">Latest</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="mb-1">
                    <strong>Size :</strong>{" "}
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p className="mb-1">
                    <strong>Uploaded At :</strong>{" "}
                    {new Date(selectedFile.uploadedAt).toLocaleString()}
                  </p>
                  <p
                    className="text-primary mb-3"
                    style={{ fontSize: 12, wordBreak: "break-all" }}
                  >
                    <strong>Key :</strong> {selectedFile.key}
                  </p>

                  <a
                    href={selectedFile.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sec"
                  >
                    Download
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default InsurerDocuments;